import {EmployeeQuota} from "../pages/serviceModules/TimeTracking/TimeTrackingOverviewPage";
import {useState} from "react";
import {timeTrackingApi} from "../api/timeTrackingApi";
import {absenceApi} from "../api/absenceApi";

type Format = "xlsx" | "csv" | "pdf";
type ExportMode = "timetracking" | "absences";

export const useExport = (
    serviceModuleId: number,
    employees: { employee: {id: string}}[],
    mode: ExportMode
) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
    const [format, setFormat] = useState<Format>("xlsx");
    const [employeeIds, setEmployeeIds] = useState<string[]>([]);

    const openExport = (ids: string[]) => {
        setEmployeeIds(ids);
        setOpen(true);
    };

    const buildPayload = () => {
        if (!employeeIds.length || !year || !month) {
            throw new Error("Invalid export state");
        }

        const startDate = new Date(Date.UTC(year, month - 1, 1));
        const endDate = new Date(Date.UTC(year, month, 0));

        return {
            employeeIds,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            fileFormat: format.toUpperCase() as "XLSX" | "CSV" | "PDF",
        };
    };

    const handleExport = async () => {
        try {
            setLoading(true);

            const result = mode === "timetracking"
                ? await timeTrackingApi.exportTimeTracking(serviceModuleId, buildPayload())
                : await absenceApi.exportAbsences(serviceModuleId, buildPayload());

            if (!result.success() || !result.result) return;

            const blob = result.result as Blob;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");

            a.href = url;
            a.download = `${mode}_${year}_${month}.${format}`;

            document.body.appendChild(a);
            a.click();

            a.remove();
            window.URL.revokeObjectURL(url);

            setOpen(false);
        } finally {
            setLoading(false);
        }
    };

    const openAllEmployees = () => {
        openExport(employees.map(e => String(e.employee.id)));
    };

    const openSingleEmployee = (id: string) => {
        openExport([id]);
    };

    return {
        open,
        setOpen,
        loading,
        year,
        setYear,
        month,
        setMonth,
        format,
        setFormat,
        openExport,
        openAllEmployees,
        openSingleEmployee,
        handleExport,
    };
};